import React from 'react'
import { createPortal } from 'react-dom'
import { CheckCircleIcon } from '@heroicons/react/24/outline'
import { Button, Heading, Text, LoadingSpinner } from '../ui'
import { EmailChipsInput } from './EmailChipsInput'
import { ContactPhoneInput } from './ContactPhoneInput'
import {
  AppointmentCreatedSummary,
  AppointmentFormField,
  AppointmentFormSection,
  appointmentFormInputClass,
} from './appointmentFormUi'
import {
  NO_ASSISTANCE_MESSAGE,
  RELATIONSHIP_TO_HOLDER_OPTIONS,
  appointmentToForm,
  combineDateAndHour,
  datePart,
  formatCpf,
  fromDatetimeLocal,
  hourPart,
  loadContactsPrefill,
  onlyDigits,
  pickCoverageForAppointment,
  preserveScheduledAt,
  toDatetimeLocal,
  uniqueEligibleCoverages,
  uniqueEligibleCoveragesForSpecialistScope,
} from './appointmentFormHelpers'
import { IaApiError } from '../../services/iaApiClient'
import { supportCenterAppointmentsService } from '../../services/supportCenterAppointments'
import { supportCenterAssistancesService } from '../../services/supportCenterAssistances'
import {
  checkSlotBookable,
  getSchedulingDateBounds,
  isSlotBookable,
  slotBookableErrorMessage,
  supportCenterParametersService,
} from '../../services/supportCenterParameters'
import type { SupportCenterSpecialization } from '../../types/supportCenterSpecialties'
import type {
  SupportCenterAgendaEvent,
  SupportCenterAttendantOption,
  SupportCenterAppointmentContactOwner,
  SupportCenterAppointmentCreateInput,
  SupportCenterAppointmentFormChannel,
} from '../../types/supportCenterAppointments'
import {
  EMPTY_APPOINTMENT_FORM,
  SUPPORT_CENTER_APPOINTMENT_CANCEL_CHANNEL_OPTIONS,
  SUPPORT_CENTER_APPOINTMENT_FORM_CHANNEL_OPTIONS,
  appointmentChannelLabel,
  type SupportCenterAppointmentCancelChannel,
  type SupportCenterAppointmentFormState,
} from '../../types/supportCenterAppointments'
import { formatDateTime } from './dashboardHelpers'
import type { SupportCenterCoverageScope } from '../../types/supportCenterAssistances'
import {
  DEFAULT_SCHEDULING_LIMITS,
  type SupportCenterSchedulingLimits,
} from '../../types/supportCenterParameters'
import type { SupportCenterElegibilidadeProfissional } from '../../types/supportCenterAgenda'
import { supportCenterCancelReasonsService } from '../../services/supportCenterCancelReasons'
import type { SupportCenterCancelReason } from '../../types/supportCenterCancelReasons'

type SummaryRow = { label: string; value: string; wide?: boolean; href?: string }

type CreatedAppointmentSummary = {
  rows: SummaryRow[]
  /** Preenchido no reagendamento: dados da consulta que foi cancelada. */
  cancelledRows?: SummaryRow[]
}

function buildAppointmentSummaryRows(input: {
  scheduledAt: string
  channel: string | null | undefined
  assistanceLabel: string
  specializationName: string
  professionalName: string
  holderName: string
  holderCpf: string
  patientName: string
  patientCpf: string
  patientIsHolder: boolean
  relationship: string
  patientPhone: string
  patientEmail: string
  meetingLink: string
}): SummaryRow[] {
  const rows: SummaryRow[] = [
    { label: 'Data e horário', value: formatDateTime(input.scheduledAt), wide: true },
    { label: 'Canal', value: appointmentChannelLabel(input.channel) },
    { label: 'Assistência', value: input.assistanceLabel },
    { label: 'Especialidade', value: input.specializationName },
    { label: 'Profissional', value: input.professionalName },
    { label: 'Segurado', value: input.holderName },
    { label: 'CPF do segurado', value: formatCpf(input.holderCpf) },
    {
      label: 'Paciente',
      value: input.patientIsHolder ? input.holderName : input.patientName,
    },
    { label: 'CPF do paciente', value: formatCpf(input.patientCpf) },
  ]
  if (!input.patientIsHolder && input.relationship.trim()) {
    rows.push({ label: 'Parentesco', value: input.relationship.trim() })
  }
  rows.push(
    { label: 'Telefone do paciente', value: input.patientPhone },
    { label: 'E-mail do paciente', value: input.patientEmail, wide: true }
  )
  const meetingLink = input.meetingLink.trim()
  if (meetingLink) {
    rows.push({ label: 'Link da reunião', value: meetingLink, wide: true, href: meetingLink })
  }
  return rows
}

type AppointmentFormModalProps = {
  open: boolean
  mode: 'create' | 'edit'
  initial?: SupportCenterAgendaEvent | null
  initialScheduledAt?: string
  specializations: SupportCenterSpecialization[]
  /**
   * Vínculos do usuário em support_center_specialty_users.
   * Sem "ver valores": filtrar especialidades e assistências do profissional.
   * Com "ver valores": passar [] (só elegibilidade do CPF).
   */
  userSpecializationIds?: string[]
  /**
   * Sem "ver valores": só marca para o próprio usuário e restringe
   * especialidades/assistências aos vínculos dele (mesmo com lista vazia).
   */
  restrictToOwnProfessional?: boolean
  currentUserId: string | null
  canSubmit: boolean
  onClose: () => void
  onSaved: (event: SupportCenterAgendaEvent) => void
}

export const AppointmentFormModal: React.FC<AppointmentFormModalProps> = ({
  open,
  mode,
  initial,
  initialScheduledAt,
  specializations,
  userSpecializationIds = [],
  restrictToOwnProfessional = false,
  currentUserId,
  canSubmit,
  onClose,
  onSaved,
}) => {
  const [form, setForm] = React.useState<SupportCenterAppointmentFormState>(EMPTY_APPOINTMENT_FORM)
  const [createdSummary, setCreatedSummary] = React.useState<CreatedAppointmentSummary | null>(null)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [warning, setWarning] = React.useState<string | null>(null)
  const [cancelReasonId, setCancelReasonId] = React.useState('')
  const [cancellationChannel, setCancellationChannel] = React.useState<
    '' | SupportCenterAppointmentCancelChannel
  >('')
  const [cancelReasons, setCancelReasons] = React.useState<SupportCenterCancelReason[]>([])
  const [loadingCancelReasons, setLoadingCancelReasons] = React.useState(false)
  const [lookingUp, setLookingUp] = React.useState<'holder' | 'patient' | null>(null)
  const [contactEmailSuggestions, setContactEmailSuggestions] = React.useState<string[]>([])
  const [contactPhoneSuggestions, setContactPhoneSuggestions] = React.useState<string[]>([])
  const [professionals, setProfessionals] = React.useState<SupportCenterAttendantOption[]>([])
  const [availableHours, setAvailableHours] = React.useState<
    Array<{ horario: string; scheduled_at: string }>
  >([])
  const [loadingSlots, setLoadingSlots] = React.useState(false)
  const [slotsError, setSlotsError] = React.useState<string | null>(null)
  /** Profissionais livres no horário travado (null = filtro ainda não aplicado / sem lock). */
  const [freeProfessionalIds, setFreeProfessionalIds] = React.useState<Set<string> | null>(null)
  const [loadingSlotFilter, setLoadingSlotFilter] = React.useState(false)
  const [slotFilterWarning, setSlotFilterWarning] = React.useState<string | null>(null)
  const slotAvailabilityCacheRef = React.useRef<
    Map<string, Array<{ horario: string; scheduled_at: string }>>
  >(new Map())
  /** null = ainda não validou elegibilidade; [] = sem match / erro */
  const [eligibleSpecializationIds, setEligibleSpecializationIds] = React.useState<string[] | null>(
    null
  )
  /** Catálogo + pessoas vindos de GET /elegibilidade (uma única request). */
  const [eligibleEspecializacoes, setEligibleEspecializacoes] = React.useState<
    SupportCenterElegibilidadeProfissional[] | null
  >(null)
  const [schedulingLimits, setSchedulingLimits] =
    React.useState<SupportCenterSchedulingLimits>(DEFAULT_SCHEDULING_LIMITS)
  /** Especialidades do catálogo vinculadas à assistência selecionada (null = ainda não carregou). */
  const [catalogSpecIdsForAssistance, setCatalogSpecIdsForAssistance] = React.useState<
    string[] | null
  >(null)
  /** Cobertura da assistência do catálogo (passo 2). */
  const [selectedCoverageScope, setSelectedCoverageScope] =
    React.useState<SupportCenterCoverageScope | null>(null)
  const step2FirstControlRef = React.useRef<HTMLButtonElement>(null)
  const pendingStep2FocusRef = React.useRef(false)
  /** CPF já consultado neste preenchimento. Evita segunda busca no Tab/blur. */
  const lookedUpHolderCpfRef = React.useRef<string | null>(null)
  const lookedUpPatientCpfRef = React.useRef<string | null>(null)

  React.useEffect(() => {
    if (lookingUp === 'holder' || !pendingStep2FocusRef.current) return
    pendingStep2FocusRef.current = false
    step2FirstControlRef.current?.focus()
  }, [lookingUp])

  const applyContactPrefill = React.useCallback((taxId: string, forceReplace = false) => {
    if (onlyDigits(taxId).length !== 11) {
      setContactPhoneSuggestions([])
      setContactEmailSuggestions([])
      return
    }
    void loadContactsPrefill(taxId)
      .then(({ phones, emails }) => {
        setContactPhoneSuggestions(phones)
        setContactEmailSuggestions(emails)
        setForm((prev) => ({
          ...prev,
          patient_phone:
            forceReplace || !prev.patient_phone.trim()
              ? phones[0]
                ? phones[0]
                : forceReplace
                  ? ''
                  : prev.patient_phone
              : prev.patient_phone,
          client_emails:
            forceReplace || prev.client_emails.length === 0
              ? emails.length > 0
                ? [emails[0]]
                : forceReplace
                  ? []
                  : prev.client_emails
              : prev.client_emails,
        }))
      })
      .catch(() => {
        setContactPhoneSuggestions([])
        setContactEmailSuggestions([])
      })
  }, [])

  const selectedDate = datePart(form.scheduled_at)
  const selectedHour = hourPart(form.scheduled_at)
  /** Clique na grade só pré-preenche; data e horário permanecem editáveis. */
  const slotLocked = false
  const dateBounds = React.useMemo(
    () => getSchedulingDateBounds(schedulingLimits),
    [schedulingLimits]
  )
  const selectedProfessionalEmail =
    professionals.find((p) => p.id === form.professional_user_id)?.email?.trim() || ''

  /** Sem ver valores: sempre restringe às especialidades do profissional (mesmo sem vínculos). */
  const isSpecialistScope = restrictToOwnProfessional || userSpecializationIds.length > 0
  /** Sem ver valores OU profissional com vínculos: profissional = usuário logado. */
  const lockProfessionalToSelf = restrictToOwnProfessional || userSpecializationIds.length > 0
  const userSpecSet = React.useMemo(
    () => new Set(userSpecializationIds),
    [userSpecializationIds]
  )
  /** Códigos de assistência do catálogo nas especialidades do profissional. */
  const allowedAssistanceCodes = React.useMemo(() => {
    if (!isSpecialistScope) return null
    const codes = new Set<string>()
    for (const spec of specializations) {
      if (!userSpecSet.has(spec.id)) continue
      for (const assistance of spec.assistances ?? []) {
        const code = assistance.code.trim()
        if (code) codes.add(code)
      }
    }
    return codes
  }, [isSpecialistScope, specializations, userSpecSet])

  const resolveScopedCoverages = React.useCallback(
    (profissionais: SupportCenterElegibilidadeProfissional[] | null | undefined) => {
      if (!isSpecialistScope) return uniqueEligibleCoverages(profissionais)
      return uniqueEligibleCoveragesForSpecialistScope(
        profissionais,
        userSpecSet,
        allowedAssistanceCodes ?? undefined
      )
    },
    [isSpecialistScope, userSpecSet, allowedAssistanceCodes]
  )

  const selectableSpecializations = React.useMemo(() => {
    if (!eligibleSpecializationIds) return []
    const eligible = new Set(eligibleSpecializationIds)
    const assistanceCode = form.assistance_code.trim()
    if (!assistanceCode) return []
    if (catalogSpecIdsForAssistance === null) return []
    const catalogSet = new Set(catalogSpecIdsForAssistance)
    return specializations.filter((spec) => {
      if (!eligible.has(spec.id)) return false
      if (isSpecialistScope && !userSpecSet.has(spec.id)) return false
      if (!catalogSet.has(spec.id)) return false
      const elegSpec = eligibleEspecializacoes?.find((item) => item.id === spec.id)
      const hasCoverage = (elegSpec?.coberturas ?? []).some(
        (cobertura) => cobertura.code.trim() === assistanceCode
      )
      if (!hasCoverage) return false
      if (slotLocked && freeProfessionalIds) {
        const pros = elegSpec?.profissionais ?? []
        if (!pros.some((p) => freeProfessionalIds.has(p.id))) return false
      }
      return true
    })
  }, [
    eligibleSpecializationIds,
    specializations,
    isSpecialistScope,
    userSpecSet,
    slotLocked,
    freeProfessionalIds,
    eligibleEspecializacoes,
    form.assistance_code,
    catalogSpecIdsForAssistance,
  ])

  const eligibilityReady = eligibleSpecializationIds !== null
  const hasEligibleSpecs = selectableSpecializations.length > 0
  /** Só permite criar/reagendar após elegibilidade OK com ao menos um profissional no escopo. */
  const canSchedule = hasEligibleSpecs
  const submitDisabled =
    saving ||
    !canSubmit ||
    !canSchedule ||
    lookingUp !== null ||
    (mode === 'edit' && (!cancelReasonId || !cancellationChannel))

  const holderCoverages = React.useMemo(
    () => resolveScopedCoverages(eligibleEspecializacoes),
    [eligibleEspecializacoes, resolveScopedCoverages]
  )
  const showAssistanceStep =
    lookingUp !== 'holder' &&
    eligibleEspecializacoes !== null &&
    Boolean(form.holder_name.trim() || eligibleEspecializacoes.length > 0)

  const applyHolderEligibilitySuccess = React.useCallback(
    (
      digits: string,
      pessoa: { name: string | null; captured_birth_date: string | null },
      profissionais: SupportCenterElegibilidadeProfissional[]
    ) => {
      const profissionalIds = profissionais.map((spec) => spec.id)
      setEligibleSpecializationIds(profissionalIds)
      setEligibleEspecializacoes(profissionais)

      const scopedIds = specializations
        .filter((spec) => {
          if (!profissionalIds.includes(spec.id)) return false
          if (isSpecialistScope && !userSpecSet.has(spec.id)) return false
          return true
        })
        .map((s) => s.id)
      const scopedSet = new Set(scopedIds)

      setForm((prev) => {
        const name = pessoa.name?.trim() || ''
        const birth = pessoa.captured_birth_date?.slice(0, 10) ?? ''
        const coverages = resolveScopedCoverages(profissionais)
        const keepAssistance = Boolean(
          prev.assistance_code &&
            coverages.some((cobertura) => cobertura.code.trim() === prev.assistance_code.trim())
        )
        const autoAssistance =
          !keepAssistance && coverages.length === 1
            ? coverages[0].code
            : keepAssistance
              ? prev.assistance_code
              : ''
        const keepSpec = Boolean(
          autoAssistance &&
            prev.specialization_id &&
            scopedSet.has(prev.specialization_id) &&
            (profissionais.find((item) => item.id === prev.specialization_id)?.coberturas ?? []).some(
              (cobertura) => cobertura.code.trim() === autoAssistance.trim()
            )
        )
        return {
          ...prev,
          holder_cpf: formatCpf(digits),
          holder_name: name,
          holder_birth_date: birth,
          ...(prev.patient_is_insured
            ? {
                patient_cpf: formatCpf(digits),
                patient_name: name,
                patient_birth_date: birth,
                contact_owner: 'holder' as SupportCenterAppointmentContactOwner,
              }
            : {}),
          assistance_code: autoAssistance,
          specialization_id: keepSpec ? prev.specialization_id : '',
          professional_user_id: keepSpec
            ? lockProfessionalToSelf
              ? currentUserId ?? ''
              : prev.professional_user_id
            : '',
          scheduled_at:
            slotLocked && hourPart(prev.scheduled_at)
              ? prev.scheduled_at
              : keepSpec
                ? prev.scheduled_at
                : datePart(prev.scheduled_at)
                  ? `${datePart(prev.scheduled_at)}T`
                  : '',
        }
      })

      void loadContactsPrefill(digits)
        .then(({ phones, emails }) => {
          setForm((prev) => {
            if (!prev.patient_is_insured) return prev
            void Promise.resolve().then(() => {
              setContactPhoneSuggestions(phones)
              setContactEmailSuggestions(emails)
            })
            return {
              ...prev,
              patient_phone: prev.patient_phone.trim()
                ? prev.patient_phone
                : phones[0] || prev.patient_phone,
              client_emails:
                prev.client_emails.length > 0
                  ? prev.client_emails
                  : emails.length > 0
                    ? [emails[0]]
                    : prev.client_emails,
            }
          })
        })
        .catch(() => {
          /* prefill opcional */
        })
    },
    [specializations, currentUserId, isSpecialistScope, userSpecSet, slotLocked, lockProfessionalToSelf, resolveScopedCoverages]
  )

  const clearHolderEligibility = React.useCallback(
    (digits?: string) => {
      setEligibleSpecializationIds([])
      setEligibleEspecializacoes([])
      setProfessionals([])
      setContactPhoneSuggestions([])
      setContactEmailSuggestions([])
      setForm((prev) => ({
        ...prev,
        holder_name: '',
        holder_birth_date: '',
        assistance_code: '',
        specialization_id: '',
        professional_user_id: '',
        client_emails: [],
        patient_phone: '',
        scheduled_at: preserveScheduledAt(prev.scheduled_at, slotLocked),
        ...(prev.patient_is_insured
          ? {
              patient_name: '',
              patient_birth_date: '',
              ...(digits ? { patient_cpf: formatCpf(digits) } : {}),
            }
          : {}),
      }))
    },
    [slotLocked]
  )

  const lookupHolderEligibility = React.useCallback(
    async (rawCpf: string) => {
      const digits = onlyDigits(rawCpf).slice(0, 11)
      if (digits.length !== 11) return
      if (lookedUpHolderCpfRef.current === digits) return
      lookedUpHolderCpfRef.current = digits
      setLookingUp('holder')
      setError(null)
      try {
        const result = await supportCenterAppointmentsService.getElegibilidade(digits)
        if (lookedUpHolderCpfRef.current !== digits) return
        applyHolderEligibilitySuccess(digits, result.pessoa, result.especialidades)
        const scopedCount = specializations.filter((spec) => {
          if (!result.especialidadeIds.includes(spec.id)) return false
          if (isSpecialistScope && !userSpecSet.has(spec.id)) return false
          return true
        }).length
        if (scopedCount === 0) {
          setError(NO_ASSISTANCE_MESSAGE)
        }
      } catch (e) {
        if (lookedUpHolderCpfRef.current !== digits) return
        lookedUpHolderCpfRef.current = null
        clearHolderEligibility(digits)
        const message =
          e instanceof Error && e.message.trim()
            ? e.message.trim()
            : 'Não foi possível validar elegibilidade do CPF.'
        setError(message)
      } finally {
        if (lookedUpHolderCpfRef.current === digits || lookedUpHolderCpfRef.current === null) {
          setLookingUp(null)
        }
      }
    },
    [
      applyHolderEligibilitySuccess,
      clearHolderEligibility,
      specializations,
      isSpecialistScope,
      userSpecSet,
    ]
  )

  const lookupPatientPessoa = React.useCallback(async (rawCpf: string) => {
    const digits = onlyDigits(rawCpf).slice(0, 11)
    if (digits.length !== 11) return
    if (lookedUpPatientCpfRef.current === digits) return
    lookedUpPatientCpfRef.current = digits
    setLookingUp('patient')
    setError(null)
    try {
      const person = await supportCenterAppointmentsService.getPessoaCpf(digits)
      if (lookedUpPatientCpfRef.current !== digits) return
      setForm((prev) => ({
        ...prev,
        patient_cpf: formatCpf(digits),
        patient_name: person.name?.trim() || '',
        patient_birth_date: person.captured_birth_date?.slice(0, 10) ?? '',
      }))
      void loadContactsPrefill(digits)
        .then(({ phones, emails }) => {
          setForm((prev) => {
            if (prev.patient_is_insured) return prev
            void Promise.resolve().then(() => {
              setContactPhoneSuggestions(phones)
              setContactEmailSuggestions(emails)
            })
            return {
              ...prev,
              patient_phone: prev.patient_phone.trim()
                ? prev.patient_phone
                : phones[0] || prev.patient_phone,
              client_emails:
                prev.client_emails.length > 0
                  ? prev.client_emails
                  : emails.length > 0
                    ? [emails[0]]
                    : prev.client_emails,
            }
          })
        })
        .catch(() => {
          /* prefill opcional */
        })
    } catch (e) {
      if (lookedUpPatientCpfRef.current !== digits) return
      // Dependente pode não existir na base. Não bloquear o agendamento por isso:
      // nome e nascimento ficam para o atendente informar.
      const personNotFound =
        (e instanceof IaApiError && e.status === 424) ||
        (e instanceof Error && /pessoa não encontrada/i.test(e.message))
      if (personNotFound) return
      lookedUpPatientCpfRef.current = null
      setForm((prev) => ({
        ...prev,
        patient_name: '',
        patient_birth_date: '',
      }))
      setError(e instanceof Error ? e.message : 'Não foi possível buscar o CPF do paciente.')
    } finally {
      if (lookedUpPatientCpfRef.current === digits || lookedUpPatientCpfRef.current === null) {
        setLookingUp(null)
      }
    }
  }, [])

  const lookupHolderRef = React.useRef(lookupHolderEligibility)
  lookupHolderRef.current = lookupHolderEligibility
  const lookupPatientRef = React.useRef(lookupPatientPessoa)
  lookupPatientRef.current = lookupPatientPessoa

  React.useEffect(() => {
    if (!open) {
      setCreatedSummary(null)
      lookedUpHolderCpfRef.current = null
      lookedUpPatientCpfRef.current = null
      return
    }
    lookedUpHolderCpfRef.current = null
    lookedUpPatientCpfRef.current = null
    setCreatedSummary(null)
    setError(null)
    setWarning(null)
    setSlotsError(null)
    setSlotFilterWarning(null)
    setFreeProfessionalIds(null)
    slotAvailabilityCacheRef.current = new Map()
    setEligibleSpecializationIds(null)
    setEligibleEspecializacoes(null)
    setCatalogSpecIdsForAssistance(null)
    setSelectedCoverageScope(null)
    setProfessionals([])
    setContactPhoneSuggestions([])
    setContactEmailSuggestions([])
    setCancelReasonId('')
    setCancellationChannel('')
    setCancelReasons([])
    void supportCenterParametersService.getSchedulingLimits().then(setSchedulingLimits)
    if (mode === 'edit' && initial) {
      setLoadingCancelReasons(true)
      void supportCenterCancelReasonsService
        .listByKind('atendimento')
        .then((list) => {
          setCancelReasons(list)
          if (list.length === 0) {
            setError(
              'Nenhum motivo cadastrado. Cadastre em Parâmetros > Motivos de cancelamento > Atendimento.'
            )
          }
        })
        .catch((e) => {
          setCancelReasons([])
          setError(
            e instanceof Error ? e.message : 'Não foi possível carregar os motivos de cancelamento.'
          )
        })
        .finally(() => setLoadingCancelReasons(false))
      const next = appointmentToForm(initial)
      setForm(next)
      const holderDigits = onlyDigits(next.holder_cpf)
      if (holderDigits.length === 11) {
        void lookupHolderRef.current(holderDigits)
      }
      if (!next.patient_is_insured && onlyDigits(next.patient_cpf).length === 11) {
        void lookupPatientRef.current(next.patient_cpf)
      }
    } else {
      setLoadingCancelReasons(false)
      setForm({
        ...EMPTY_APPOINTMENT_FORM,
        specialization_id: '',
        professional_user_id: '',
        scheduled_at: initialScheduledAt ? toDatetimeLocal(initialScheduledAt) : '',
        patient_is_insured: true,
      })
    }
  }, [open, mode, initial, initialScheduledAt, currentUserId])

  // Com horário travado: quais profissionais estão livres naquele slot.
  React.useEffect(() => {
    if (!open || !slotLocked || !selectedDate || !selectedHour || !eligibleEspecializacoes) {
      if (!slotLocked) {
        setFreeProfessionalIds(null)
        setLoadingSlotFilter(false)
        setSlotFilterWarning(null)
      }
      return
    }

    let cancelled = false
    void (async () => {
      setLoadingSlotFilter(true)
      setSlotFilterWarning(null)
      try {
        const candidates = new Map<string, { id: string; email: string }>()
        for (const spec of eligibleEspecializacoes) {
          if (isSpecialistScope && !userSpecSet.has(spec.id)) continue
          for (const pro of spec.profissionais ?? []) {
            if (!pro.id) continue
            if (lockProfessionalToSelf && currentUserId && pro.id !== currentUserId) continue
            candidates.set(pro.id, { id: pro.id, email: pro.email?.trim() || '' })
          }
        }

        const now = new Date()
        const free = new Set<string>()
        await Promise.all(
          [...candidates.values()].map(async (pro) => {
            const cacheKey = `${pro.id}|${selectedDate}`
            let slots = slotAvailabilityCacheRef.current.get(cacheKey)
            if (!slots) {
              slots = await supportCenterAppointmentsService.listAvailableSlots(
                { userId: pro.id, email: pro.email },
                selectedDate
              )
              slotAvailabilityCacheRef.current.set(cacheKey, slots)
            }
            const match = slots.some((s) => {
              const hour = (s.horario || '').slice(0, 5)
              return (
                (hour === selectedHour || hour.slice(0, 2) === selectedHour.slice(0, 2)) &&
                isSlotBookable(s.scheduled_at, schedulingLimits, now)
              )
            })
            if (match) free.add(pro.id)
          })
        )

        if (cancelled) return
        setFreeProfessionalIds(free)
        if (free.size === 0) {
          setSlotFilterWarning(
            lockProfessionalToSelf
              ? 'Você não está livre neste horário. Escolha outro slot na agenda.'
              : 'Ninguém livre neste horário. Escolha outro slot na agenda.'
          )
        }
      } catch (e) {
        if (!cancelled) {
          setFreeProfessionalIds(new Set())
          setSlotFilterWarning(
            e instanceof Error
              ? e.message
              : 'Não foi possível verificar disponibilidade neste horário.'
          )
        }
      } finally {
        if (!cancelled) setLoadingSlotFilter(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [
    open,
    slotLocked,
    selectedDate,
    selectedHour,
    eligibleEspecializacoes,
    isSpecialistScope,
    lockProfessionalToSelf,
    userSpecSet,
    currentUserId,
    schedulingLimits,
  ])

  // Catálogo — especialidades e cobertura da assistência escolhida.
  React.useEffect(() => {
    const code = form.assistance_code.trim()
    if (!open || !code) {
      setCatalogSpecIdsForAssistance(null)
      setSelectedCoverageScope(null)
      return
    }
    let cancelled = false
    setCatalogSpecIdsForAssistance(null)
    setSelectedCoverageScope(null)
    void Promise.all([
      supportCenterAssistancesService.listSpecializationIdsByAssistanceCode(code),
      supportCenterAssistancesService.getActiveByCode(code),
    ])
      .then(([ids, assistance]) => {
        if (cancelled) return
        setCatalogSpecIdsForAssistance(ids)
        setSelectedCoverageScope(assistance?.coverage_scope ?? null)
      })
      .catch(() => {
        if (cancelled) return
        setCatalogSpecIdsForAssistance([])
        setSelectedCoverageScope(null)
      })
    return () => {
      cancelled = true
    }
  }, [open, form.assistance_code])

  // Somente titular: trava paciente = segurado.
  React.useEffect(() => {
    if (!open || selectedCoverageScope !== 'titular') return
    if (form.patient_is_insured) return
    setForm((prev) => ({
      ...prev,
      patient_is_insured: true,
      patient_cpf: prev.holder_cpf,
      patient_name: prev.holder_name,
      patient_birth_date: prev.holder_birth_date,
      relationship_to_holder: '',
      contact_owner: 'holder',
      client_emails: [],
      patient_phone: '',
    }))
    applyContactPrefill(form.holder_cpf, true)
  }, [
    open,
    selectedCoverageScope,
    form.patient_is_insured,
    form.holder_cpf,
    applyContactPrefill,
  ])

  // Auto-seleciona especialidade quando só há uma opção para a assistência.
  const selectableSpecIdsKey = selectableSpecializations.map((spec) => spec.id).join('|')
  React.useEffect(() => {
    if (!open || !form.assistance_code.trim()) return
    if (catalogSpecIdsForAssistance === null) return
    if (loadingSlotFilter) return
    const onlySpec =
      selectableSpecializations.length === 1 ? selectableSpecializations[0].id : ''
    setForm((prev) => {
      const stillValid = Boolean(
        prev.specialization_id &&
          selectableSpecializations.some((spec) => spec.id === prev.specialization_id)
      )
      if (stillValid) return prev
      if (onlySpec === prev.specialization_id) return prev
      return {
        ...prev,
        specialization_id: onlySpec,
        professional_user_id: '',
        scheduled_at: preserveScheduledAt(prev.scheduled_at, slotLocked),
      }
    })
    // selectableSpecializations via selectableSpecIdsKey
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    form.assistance_code,
    catalogSpecIdsForAssistance,
    selectableSpecIdsKey,
    loadingSlotFilter,
    slotLocked,
  ])

  // Pessoas vêm da elegibilidade (sem nova request ao trocar o profissional).
  React.useEffect(() => {
    if (!open || !form.specialization_id || !eligibleEspecializacoes) {
      setProfessionals([])
      return
    }

    const spec = eligibleEspecializacoes.find((item) => item.id === form.specialization_id)
    let list: SupportCenterAttendantOption[] = (spec?.profissionais ?? [])
      .map((row) => ({
        id: row.id,
        name: row.name.trim() || row.email,
        email: row.email.trim(),
      }))
      .filter((row) => Boolean(row.id && row.email))
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }))

    if (slotLocked && freeProfessionalIds) {
      list = list.filter((p) => freeProfessionalIds.has(p.id))
    }

    setProfessionals(list)

    const byId = new Map(list.map((p) => [p.id, p]))
    const byEmail = new Map(list.map((p) => [p.email.trim().toLowerCase(), p]))

    let specialistMissing = false
    if (lockProfessionalToSelf && currentUserId && !byId.has(currentUserId)) {
      specialistMissing = true
      // Só alerta horário bloqueado; mensagens de "não está na lista" não usam setError
      // (ao trocar assistência a especialidade remonta e o erro piscava sob o CPF).
      if (slotLocked && freeProfessionalIds && !freeProfessionalIds.has(currentUserId)) {
        setError('Você não está livre neste horário. Escolha outro slot na agenda.')
      }
    }

    setForm((prev) => {
      let nextId = prev.professional_user_id

      if (lockProfessionalToSelf && currentUserId && byId.has(currentUserId)) {
        nextId = currentUserId
      } else if (
        list.length === 1 &&
        (!lockProfessionalToSelf || list[0].id === currentUserId)
      ) {
        // Uma única opção: seleciona automaticamente (sem furar o travamento em si mesmo).
        nextId = list[0].id
      } else if (lockProfessionalToSelf && currentUserId) {
        nextId = specialistMissing ? '' : currentUserId
      } else {
        if (nextId && !byId.has(nextId)) {
          const initialEmail =
            mode === 'edit'
              ? (initial?.professional?.email || initial?.internal_emails?.[0] || '')
                  .trim()
                  .toLowerCase()
              : ''
          nextId = (initialEmail && byEmail.get(initialEmail)?.id) || ''
        }
        if (!nextId && currentUserId && byId.has(currentUserId)) {
          nextId = currentUserId
        }
      }

      if (nextId && !byId.has(nextId)) nextId = ''

      const professional = nextId ? byId.get(nextId) : undefined
      const shouldDefaultInternal =
        mode === 'create' && prev.internal_emails.length === 0 && professional?.email
      if (nextId === prev.professional_user_id && !shouldDefaultInternal) return prev
      return {
        ...prev,
        professional_user_id: nextId,
        ...(shouldDefaultInternal
          ? { internal_emails: [String(professional.email).trim().toLowerCase()] }
          : {}),
      }
    })
  }, [
    open,
    form.specialization_id,
    eligibleEspecializacoes,
    currentUserId,
    mode,
    initial,
    lockProfessionalToSelf,
    slotLocked,
    freeProfessionalIds,
  ])

  // Carrega horários livres (hora cheia) para a data + profissional
  React.useEffect(() => {
    if (!open || !form.professional_user_id || !selectedDate) {
      setAvailableHours([])
      setSlotsError(null)
      setWarning(null)
      return
    }

    let cancelled = false
    void (async () => {
      setLoadingSlots(true)
      setSlotsError(null)
      setWarning(null)
      try {
        const cacheKey = `${form.professional_user_id}|${selectedDate}`
        let slots = slotAvailabilityCacheRef.current.get(cacheKey)
        if (!slots) {
          slots = await supportCenterAppointmentsService.listAvailableSlots(
            {
              userId: form.professional_user_id,
              email: selectedProfessionalEmail,
            },
            selectedDate
          )
          slotAvailabilityCacheRef.current.set(cacheKey, slots)
        }

        // Em edição, mantém o horário atual mesmo se a API o considerar ocupado
        const currentHour =
          mode === 'edit' && initial ? hourPart(toDatetimeLocal(initial.scheduled_at)) : ''
        const currentScheduled =
          mode === 'edit' && initial ? toDatetimeLocal(initial.scheduled_at) : ''
        const sameDay =
          mode === 'edit' && initial && datePart(toDatetimeLocal(initial.scheduled_at)) === selectedDate

        const now = new Date()
        let nextSlots = slots.filter((s) => isSlotBookable(s.scheduled_at, schedulingLimits, now))
        if (
          sameDay &&
          currentHour &&
          currentScheduled &&
          !nextSlots.some((s) => s.horario === currentHour)
        ) {
          nextSlots = [
            ...nextSlots,
            { horario: currentHour, scheduled_at: fromDatetimeLocal(currentScheduled) },
          ].sort((a, b) => a.horario.localeCompare(b.horario))
        }

        if (cancelled) return
        setAvailableHours(nextSlots)

        setForm((prev) => {
          const hour = hourPart(prev.scheduled_at)
          if (!hour) return prev
          if (
            nextSlots.some(
              (s) => s.horario === hour || s.horario.slice(0, 2) === hour.slice(0, 2)
            )
          ) {
            return prev
          }
          if (slotLocked) return prev
          // Horário escolhido ficou indisponível → limpa só a hora
          return { ...prev, scheduled_at: selectedDate ? `${selectedDate}T` : '' }
        })

        if (nextSlots.length === 0) {
          setWarning('Não há horários livres neste dia para o profissional.')
        }
      } catch (e) {
        if (!cancelled) {
          setAvailableHours([])
          setSlotsError(
            e instanceof Error ? e.message : 'Não foi possível carregar horários disponíveis.'
          )
        }
      } finally {
        if (!cancelled) setLoadingSlots(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [
    open,
    selectedProfessionalEmail,
    form.professional_user_id,
    selectedDate,
    mode,
    initial,
    schedulingLimits,
    slotLocked,
  ])

  const setSelectedDate = (dateYmd: string) => {
    if (slotLocked) return
    setForm((prev) => {
      const hour = hourPart(prev.scheduled_at)
      if (!dateYmd) return { ...prev, scheduled_at: '' }
      if (!hour) return { ...prev, scheduled_at: `${dateYmd}T` }
      return { ...prev, scheduled_at: combineDateAndHour(dateYmd, hour) }
    })
  }

  const setSelectedHour = (hourHm: string) => {
    if (slotLocked) return
    setForm((prev) => {
      const date = datePart(prev.scheduled_at)
      if (!date || !hourHm) return { ...prev, scheduled_at: date ? `${date}T` : '' }
      return { ...prev, scheduled_at: combineDateAndHour(date, hourHm) }
    })
  }
  const handleHolderCpfChange = (value: string) => {
    const formatted = formatCpf(value)
    const digits = onlyDigits(formatted)
    if (digits.length < 11) {
      lookedUpHolderCpfRef.current = null
      setEligibleSpecializationIds(null)
      setEligibleEspecializacoes(null)
      setProfessionals([])
      setContactPhoneSuggestions([])
      setContactEmailSuggestions([])
      setError(null)
    }
    setForm((prev) => ({
      ...prev,
      holder_cpf: formatted,
      ...(prev.patient_is_insured ? { patient_cpf: formatted } : {}),
      ...(digits.length < 11
        ? {
            holder_name: '',
            holder_birth_date: '',
            assistance_code: '',
            specialization_id: '',
            professional_user_id: '',
            client_emails: [],
            ...(prev.patient_is_insured ? { patient_name: '', patient_birth_date: '' } : {}),
          }
        : {}),
    }))
    if (digits.length === 11) {
      void lookupHolderEligibility(digits)
    }
  }

  const handlePatientCpfChange = (value: string) => {
    const formatted = formatCpf(value)
    const digits = onlyDigits(formatted)
    if (digits.length < 11) lookedUpPatientCpfRef.current = null
    setForm((prev) => ({
      ...prev,
      patient_cpf: formatted,
      ...(digits.length < 11 ? { patient_name: '', patient_birth_date: '' } : {}),
    }))
    if (digits.length === 11) {
      void lookupPatientPessoa(digits)
    }
  }

  const handleSubmit = async () => {
    if (!canSubmit || saving || !canSchedule) return
    setSaving(true)
    setError(null)
    try {
      const holderCpf = onlyDigits(form.holder_cpf)
      const patientCpf = form.patient_is_insured ? holderCpf : onlyDigits(form.patient_cpf)
      if (holderCpf.length !== 11) throw new Error('Informe o CPF do segurado com 11 dígitos.')
      if (patientCpf.length !== 11) throw new Error('Informe o CPF do paciente com 11 dígitos.')
      if (!form.patient_is_insured && !form.relationship_to_holder.trim()) {
        throw new Error(
          'Informe o parentesco do paciente com o segurado (obrigatório quando o paciente é diferente do titular).'
        )
      }
      if (
        !form.patient_is_insured &&
        !(RELATIONSHIP_TO_HOLDER_OPTIONS as readonly string[]).includes(
          form.relationship_to_holder.trim()
        )
      ) {
        throw new Error('Selecione o parentesco: Cônjuge ou Filho.')
      }
      if (!form.assistance_code.trim()) {
        throw new Error('Selecione a assistência.')
      }
      const channel = form.channel
      if (channel !== 'whatsapp' && channel !== 'telefone' && channel !== 'videochamada') {
        throw new Error('Selecione o canal de agendamento (Telefone, WhatsApp ou Videochamada).')
      }
      if (mode === 'edit') {
        if (!cancelReasonId.trim()) {
          throw new Error('Selecione o motivo do cancelamento do agendamento anterior.')
        }
        if (
          cancellationChannel !== 'whatsapp' &&
          cancellationChannel !== 'telefone' &&
          cancellationChannel !== 'videochamada'
        ) {
          throw new Error(
            'Selecione o canal do cancelamento do agendamento anterior (Telefone, WhatsApp ou Videochamada).'
          )
        }
      }
      if (selectedCoverageScope === 'titular' && !form.patient_is_insured) {
        throw new Error('Esta assistência cobre somente o titular.')
      }
      if (!hasEligibleSpecs) {
        throw new Error(NO_ASSISTANCE_MESSAGE)
      }
      if (!form.specialization_id) throw new Error('Selecione a especialidade.')
      if (!form.professional_user_id) throw new Error('Selecione o profissional.')
      const coberturaElegivel = pickCoverageForAppointment(
        eligibleEspecializacoes,
        form.specialization_id,
        form.assistance_code
      )
      if (
        coberturaElegivel?.codApolice == null ||
        coberturaElegivel?.codConvenio == null ||
        coberturaElegivel?.codCorretor == null ||
        coberturaElegivel?.codCliente == null
      ) {
        throw new Error(
          'Não foi possível obter apólice e convênio da elegibilidade para esta especialidade. Valide o CPF do segurado novamente.'
        )
      }
      if (!selectedDate) throw new Error('Informe a data.')
      if (!selectedHour) throw new Error('Selecione um horário disponível.')
      if (!form.scheduled_at || form.scheduled_at.length < 16) {
        throw new Error('Informe data e horário disponíveis.')
      }

      const scheduledIso = fromDatetimeLocal(form.scheduled_at)
      const keepingSameSlot =
        mode === 'edit' &&
        initial &&
        toDatetimeLocal(initial.scheduled_at) === toDatetimeLocal(form.scheduled_at)
      if (!keepingSameSlot) {
        const reason = checkSlotBookable(scheduledIso, schedulingLimits)
        const msg = slotBookableErrorMessage(reason, schedulingLimits)
        if (msg) throw new Error(msg)
        const hourStillFree = availableHours.some(
          (slot) =>
            slot.horario === selectedHour ||
            slot.horario.slice(0, 2) === selectedHour.slice(0, 2)
        )
        const lockedAndConfirmedFree =
          slotLocked &&
          Boolean(freeProfessionalIds?.has(form.professional_user_id))
        if (!hourStillFree && !lockedAndConfirmedFree) {
          throw new Error('Horário indisponível. Selecione um horário livre da lista.')
        }
      }
      const patientEmails = form.client_emails
        .map((email) => email.trim().toLowerCase())
        .filter(Boolean)
      if (patientEmails.length !== 1) {
        throw new Error(
          patientEmails.length === 0
            ? 'Informe o e-mail do paciente.'
            : 'Informe apenas um e-mail do paciente.'
        )
      }
      const patientEmail = patientEmails[0]
      const patientPhone = form.patient_phone.trim()
      if (!patientPhone) {
        throw new Error('Informe o telefone para contato.')
      }
      if (onlyDigits(patientPhone).length < 10) {
        throw new Error('Informe um telefone para contato válido (DDD + número).')
      }

      const professional = professionals.find((p) => p.id === form.professional_user_id)
      const professionalEmail = professional?.email?.trim()
      if (!professionalEmail) {
        throw new Error('Este profissional não tem e-mail cadastrado. Não é possível agendar.')
      }

      let internalEmails = [...form.internal_emails]
        .map((e) => e.trim().toLowerCase())
        .filter(Boolean)
      if (internalEmails.length === 0) {
        internalEmails = [professionalEmail]
      }

      const payloadBase: SupportCenterAppointmentCreateInput = {
        holder_cpf: holderCpf,
        holder_name: form.holder_name.trim() || `CPF ${holderCpf}`,
        holder_birth_date: form.holder_birth_date || null,
        patient_cpf: patientCpf,
        patient_name: form.patient_is_insured
          ? form.holder_name.trim() || `CPF ${holderCpf}`
          : form.patient_name.trim() || `CPF ${patientCpf}`,
        patient_birth_date: form.patient_is_insured
          ? form.holder_birth_date || null
          : form.patient_birth_date || null,
        patient_is_insured: form.patient_is_insured,
        relationship_to_holder: form.relationship_to_holder.trim() || null,
        internal_emails: internalEmails,
        patient_email: patientEmail,
        patient_phone: patientPhone,
        contact_owner: form.patient_is_insured ? 'holder' : 'patient',
        channel,
        specialization_id: form.specialization_id,
        assistance_code: form.assistance_code.trim(),
        professional_email: professionalEmail,
        professional_user_id: form.professional_user_id,
        scheduled_at: scheduledIso,
        elegibilidadeEspecialidades: eligibleEspecializacoes ?? [],
      }

      const saved =
        mode === 'edit' && initial
          ? await supportCenterAppointmentsService.update(
              { id: initial.id, ...payloadBase },
              currentUserId || '',
              cancelReasonId,
              cancellationChannel as SupportCenterAppointmentCancelChannel
            )
          : await supportCenterAppointmentsService.create(payloadBase, currentUserId)

      if (!saved?.id) {
        throw new Error('Não foi possível confirmar o agendamento. Tente novamente.')
      }

      onSaved(saved)

      const coverage = holderCoverages.find(
        (item) => item.code.trim() === form.assistance_code.trim()
      )
      const specializationName =
        saved.specialization?.name ||
        specializations.find((spec) => spec.id === form.specialization_id)?.name ||
        ''
      const professionalName =
        saved.professional?.name ||
        professionals.find((person) => person.id === form.professional_user_id)?.name ||
        ''
      const assistanceLabel = saved.assistance
        ? `${saved.assistance.name} (${saved.assistance.code})`
        : coverage
          ? `${coverage.name} (${coverage.code})`
          : form.assistance_code.trim()
      const patientIsHolder = form.patient_is_insured
      const rows = buildAppointmentSummaryRows({
        scheduledAt: saved.scheduled_at,
        channel: saved.channel || form.channel,
        assistanceLabel,
        specializationName,
        professionalName,
        holderName: saved.holder_people?.name || form.holder_name.trim(),
        holderCpf: saved.holder_cpf || form.holder_cpf,
        patientName: saved.patient_people?.name || form.patient_name.trim(),
        patientCpf: saved.patient_cpf || form.patient_cpf,
        patientIsHolder,
        relationship: form.relationship_to_holder,
        patientPhone: saved.patient_phone || form.patient_phone.trim(),
        patientEmail: saved.client_emails[0] || form.client_emails[0] || '',
        meetingLink: saved.call_link_short || saved.call_link || '',
      })

      if (mode === 'edit' && initial) {
        const cancelledIsHolder =
          onlyDigits(initial.holder_cpf) === onlyDigits(initial.patient_cpf)
        const cancelledAssistance = initial.assistance
          ? `${initial.assistance.name} (${initial.assistance.code})`
          : ''
        const cancelReasonName =
          cancelReasons.find((reason) => reason.id === cancelReasonId)?.name || ''
        const cancelledRows = buildAppointmentSummaryRows({
          scheduledAt: initial.scheduled_at,
          channel: initial.channel,
          assistanceLabel: cancelledAssistance,
          specializationName: initial.specialization?.name || '',
          professionalName: initial.professional?.name || '',
          holderName: initial.holder_people?.name || '',
          holderCpf: initial.holder_cpf,
          patientName: initial.patient_people?.name || '',
          patientCpf: initial.patient_cpf,
          patientIsHolder: cancelledIsHolder,
          relationship: initial.relationship_to_holder || '',
          patientPhone: initial.patient_phone || '',
          patientEmail: initial.client_emails?.[0] || '',
          meetingLink: initial.call_link_short || initial.call_link || '',
        })
        cancelledRows.push(
          {
            label: 'Canal do cancelamento',
            value: appointmentChannelLabel(cancellationChannel),
          },
          { label: 'Motivo do cancelamento', value: cancelReasonName, wide: true }
        )
        setCreatedSummary({ rows, cancelledRows })
        return
      }

      setCreatedSummary({ rows })
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : mode === 'edit'
            ? 'Não foi possível reagendar. Tente novamente.'
            : 'Não foi possível agendar. Tente novamente.'
      )
    } finally {
      setSaving(false)
    }
  }

  if (!open) return null

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
      <div className="relative flex max-h-[96vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-900">
        {saving && !createdSummary ? (
          <div
            className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-white/90 dark:bg-gray-900/90"
            role="status"
            aria-live="polite"
          >
            <LoadingSpinner size="lg" />
            <Text className="text-gray-800 dark:text-gray-100">
              {mode === 'edit'
                ? 'Reagendando, aguarde...'
                : 'Criando o agendamento, aguarde...'}
            </Text>
          </div>
        ) : null}
        <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            {createdSummary ? (
              <CheckCircleIcon
                className="h-7 w-7 shrink-0 text-emerald-600 dark:text-emerald-400"
                aria-hidden="true"
              />
            ) : null}
            <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
              {createdSummary
                ? createdSummary.cancelledRows
                  ? 'Reagendamento realizado com sucesso'
                  : 'Agendamento realizado com sucesso'
                : mode === 'edit'
                  ? 'Reagendar'
                  : 'Novo agendamento'}
            </Heading>
          </div>
          {createdSummary ? null : (
            <Button variant="ghost" onClick={onClose} disabled={saving}>
              Fechar
            </Button>
          )}
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5">
          {createdSummary ? (
            <div className="space-y-5">
              {createdSummary.cancelledRows ? (
                <section className="space-y-2">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                    Consulta cancelada
                  </h3>
                  <AppointmentCreatedSummary rows={createdSummary.cancelledRows} />
                </section>
              ) : null}
              <section className="space-y-2">
                {createdSummary.cancelledRows ? (
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                    Nova consulta
                  </h3>
                ) : null}
                <AppointmentCreatedSummary rows={createdSummary.rows} />
              </section>
            </div>
          ) : null}

          {!createdSummary ? (
          <>
          <AppointmentFormSection
            step={1}
            title="Segurado"
            description="Titular do plano. Informe o CPF para validar elegibilidade."
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <AppointmentFormField
                label="CPF"
                required
                className="sm:col-span-2 sm:max-w-xs"
                hint={
                  lookingUp === 'holder' ? (
                    <div className="mt-1.5 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                      <LoadingSpinner size="sm" className="justify-start" />
                      <span>Buscando dados do segurado...</span>
                    </div>
                  ) : error && eligibilityReady && !hasEligibleSpecs ? (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>
                  ) : null
                }
              >
                <input
                  className={appointmentFormInputClass}
                  value={form.holder_cpf}
                  onChange={(e) => handleHolderCpfChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Tab' || e.shiftKey) return
                    e.preventDefault()
                    const next = step2FirstControlRef.current
                    if (next && !next.disabled) {
                      next.focus()
                      return
                    }
                    pendingStep2FocusRef.current = true
                  }}
                  onBlur={() => {
                    if (onlyDigits(form.holder_cpf).length === 11) {
                      void lookupHolderEligibility(form.holder_cpf)
                    }
                  }}
                  inputMode="numeric"
                  autoComplete="off"
                  autoFocus={mode === 'create'}
                  placeholder="000.000.000-00"
                  readOnly={lookingUp === 'holder'}
                />
              </AppointmentFormField>
              <AppointmentFormField label="Nome" required>
                <input
                  className={appointmentFormInputClass}
                  value={form.holder_name}
                  disabled={lookingUp === 'holder'}
                  tabIndex={-1}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      holder_name: e.target.value,
                      ...(prev.patient_is_insured ? { patient_name: e.target.value } : {}),
                    }))
                  }
                />
              </AppointmentFormField>
              <AppointmentFormField label="Nascimento">
                <input
                  type="date"
                  className={appointmentFormInputClass}
                  value={form.holder_birth_date}
                  disabled={lookingUp === 'holder'}
                  tabIndex={-1}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      holder_birth_date: e.target.value,
                      ...(prev.patient_is_insured ? { patient_birth_date: e.target.value } : {}),
                    }))
                  }
                />
              </AppointmentFormField>
            </div>
          </AppointmentFormSection>

          <AppointmentFormSection
            step={2}
            title="Assistência"
            description="Escolha a cobertura elegível do segurado para este agendamento."
          >
            {!showAssistanceStep ? (
              <Text size="sm" color="muted">
                Informe o CPF do segurado no passo 1 para listar as assistências.
              </Text>
            ) : holderCoverages.length === 0 ? (
              <p className="text-sm text-red-600 dark:text-red-400">
                {isSpecialistScope
                  ? 'Nenhuma assistência elegível vinculada às suas especialidades para este segurado.'
                  : 'Nenhuma cobertura retornada para este segurado.'}
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {holderCoverages.map((cobertura, index) => {
                  const selected = form.assistance_code.trim() === cobertura.code.trim()
                  return (
                    <button
                      key={cobertura.code}
                      type="button"
                      ref={index === 0 ? step2FirstControlRef : undefined}
                      onClick={() => {
                        setForm((prev) => {
                          if (prev.assistance_code.trim() === cobertura.code.trim()) return prev
                          return {
                            ...prev,
                            assistance_code: cobertura.code,
                            specialization_id: '',
                            professional_user_id: '',
                            scheduled_at: preserveScheduledAt(prev.scheduled_at, slotLocked),
                          }
                        })
                        setError(null)
                      }}
                      className={[
                        'rounded-lg border px-3 py-2.5 text-left text-sm transition-colors',
                        selected
                          ? 'border-primary-500 bg-primary-50 text-primary-900 dark:bg-primary-900/30 dark:text-primary-100 dark:border-primary-400'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200',
                      ].join(' ')}
                    >
                      <span className="font-medium">{cobertura.name}</span>
                      <span className="mt-0.5 block text-xs opacity-80">
                        Código {cobertura.code}
                      </span>
                    </button>
                  )
                })}
              </div>
            )}
          </AppointmentFormSection>

          <AppointmentFormSection
            step={3}
            title="Paciente e contato"
            description="Quem será atendido e como entrar em contato."
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button
                type="button"
                disabled={lookingUp === 'holder' || !form.assistance_code.trim()}
                onClick={() => {
                  setForm((prev) => ({
                    ...prev,
                    patient_is_insured: true,
                    patient_cpf: prev.holder_cpf,
                    patient_name: prev.holder_name,
                    patient_birth_date: prev.holder_birth_date,
                    relationship_to_holder: '',
                    contact_owner: 'holder',
                    client_emails: [],
                    patient_phone: '',
                  }))
                  applyContactPrefill(form.holder_cpf, true)
                }}
                className={[
                  'rounded-lg border px-3 py-2.5 text-left text-sm transition-colors',
                  form.patient_is_insured
                    ? 'border-primary-500 bg-primary-50 text-primary-900 dark:bg-primary-900/30 dark:text-primary-100 dark:border-primary-400'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200',
                  !form.assistance_code.trim() ? 'opacity-60 cursor-not-allowed' : '',
                ].join(' ')}
              >
                <span className="font-medium">O próprio segurado</span>
                <span className="mt-0.5 block text-xs opacity-80">Paciente é o titular do plano</span>
              </button>
              <button
                type="button"
                disabled={
                  lookingUp === 'holder' ||
                  !form.assistance_code.trim() ||
                  selectedCoverageScope === 'titular'
                }
                title={
                  selectedCoverageScope === 'titular'
                    ? 'Assistência somente titular'
                    : undefined
                }
                onClick={() => {
                  if (selectedCoverageScope === 'titular') return
                  lookedUpPatientCpfRef.current = null
                  setForm((prev) => ({
                    ...prev,
                    patient_is_insured: false,
                    patient_cpf: '',
                    patient_name: '',
                    patient_birth_date: '',
                    relationship_to_holder: '',
                    contact_owner: 'patient',
                    client_emails: [],
                    patient_phone: '',
                  }))
                  setContactPhoneSuggestions([])
                  setContactEmailSuggestions([])
                }}
                className={[
                  'rounded-lg border px-3 py-2.5 text-left text-sm transition-colors',
                  !form.patient_is_insured
                    ? 'border-primary-500 bg-primary-50 text-primary-900 dark:bg-primary-900/30 dark:text-primary-100 dark:border-primary-400'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200',
                  !form.assistance_code.trim() || selectedCoverageScope === 'titular'
                    ? 'opacity-60 cursor-not-allowed'
                    : '',
                ].join(' ')}
              >
                <span className="font-medium">Dependentes</span>
                <span className="mt-0.5 block text-xs opacity-80">
                  Cônjuge/Companheiro(a) ou Filho
                </span>
              </button>
            </div>
            {!form.assistance_code.trim() ? (
              <Text size="xs" color="muted" className="mt-1">
                Selecione a assistência no passo 2.
              </Text>
            ) : selectedCoverageScope === 'titular' ? (
              <Text size="xs" color="muted" className="mt-1">
                Assistência somente titular.
              </Text>
            ) : null}

            {form.patient_is_insured ? null : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                <AppointmentFormField
                  label="CPF do paciente"
                  required
                  hint={
                    lookingUp === 'patient' ? (
                      <div className="mt-1.5 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                        <LoadingSpinner size="sm" className="justify-start" />
                        <span>Buscando dados do paciente...</span>
                      </div>
                    ) : null
                  }
                >
                  <input
                    className={appointmentFormInputClass}
                    value={form.patient_cpf}
                    onChange={(e) => handlePatientCpfChange(e.target.value)}
                    onBlur={() => {
                      if (onlyDigits(form.patient_cpf).length === 11) {
                        void lookupPatientPessoa(form.patient_cpf)
                      }
                    }}
                    inputMode="numeric"
                    autoComplete="off"
                    placeholder="000.000.000-00"
                    disabled={lookingUp === 'patient'}
                  />
                </AppointmentFormField>
                <AppointmentFormField label="Parentesco com o segurado" required>
                  <select
                    className={appointmentFormInputClass}
                    value={
                      RELATIONSHIP_TO_HOLDER_OPTIONS.includes(
                        form.relationship_to_holder as (typeof RELATIONSHIP_TO_HOLDER_OPTIONS)[number]
                      )
                        ? form.relationship_to_holder
                        : ''
                    }
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, relationship_to_holder: e.target.value }))
                    }
                    required
                  >
                    <option value="">Selecione</option>
                    {RELATIONSHIP_TO_HOLDER_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </AppointmentFormField>
                <AppointmentFormField label="Nome" required>
                  <input
                    className={appointmentFormInputClass}
                    value={form.patient_name}
                    disabled={lookingUp === 'patient'}
                    onChange={(e) => setForm((prev) => ({ ...prev, patient_name: e.target.value }))}
                  />
                </AppointmentFormField>
                <AppointmentFormField label="Nascimento">
                  <input
                    type="date"
                    className={appointmentFormInputClass}
                    value={form.patient_birth_date}
                    disabled={lookingUp === 'patient'}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, patient_birth_date: e.target.value }))
                    }
                  />
                </AppointmentFormField>
              </div>
            )}

            <div className="space-y-3 border-t border-gray-200 dark:border-gray-700 pt-3 mt-1">
              <Text size="sm" className="font-medium text-gray-800 dark:text-gray-200">
                Contato do paciente
              </Text>

              <ContactPhoneInput
                label="Telefone do paciente"
                value={form.patient_phone}
                onChange={(value) => setForm((prev) => ({ ...prev, patient_phone: value }))}
                suggestions={contactPhoneSuggestions}
                required
                disabled={lookingUp !== null}
                placeholder="(00) 00000-0000"
                helpText={
                  contactPhoneSuggestions.length > 0
                    ? 'Selecione um cadastrado ou digite um novo. O telefone do paciente é enviado no agendamento e a API grava o contato.'
                    : 'Digite o telefone do paciente. Ele é enviado no agendamento e a API grava o contato.'
                }
              />

              <EmailChipsInput
                label="E-mail do paciente"
                values={form.client_emails}
                onChange={(values) =>
                  setForm((prev) => ({ ...prev, client_emails: values.slice(0, 1) }))
                }
                suggestions={contactEmailSuggestions}
                maxEmails={1}
                required
                disabled={lookingUp !== null}
                placeholder="Digite o e-mail"
                helpText={
                  contactEmailSuggestions.length > 0
                    ? 'Selecione um cadastrado ou digite um novo. O e-mail do paciente é enviado no agendamento e a API grava o contato.'
                    : 'Digite o e-mail do paciente. Ele é enviado no agendamento e a API grava o contato.'
                }
              />
            </div>
          </AppointmentFormSection>

          <AppointmentFormSection
            step={4}
            title="Agenda"
            description={
              slotLocked
                ? 'Horário selecionado na agenda. Escolha a especialidade e o profissional disponíveis neste horário.'
                : 'Especialidade vinculada à assistência, profissional e horário.'
            }
          >
            {slotLocked && (
              <div className="rounded-lg border border-primary-300 bg-primary-50 dark:bg-primary-900/20 dark:border-primary-700 px-3 py-2 text-sm text-primary-900 dark:text-primary-100">
                Horário selecionado na agenda:{' '}
                <span className="font-semibold">
                  {selectedDate ? selectedDate.split('-').reverse().join('/') : '—'} às{' '}
                  {selectedHour || '—'}
                </span>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <AppointmentFormField label="Canal de agendamento" required className="sm:col-span-2 sm:max-w-xs">
                <select
                  className={appointmentFormInputClass}
                  value={form.channel}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      channel: e.target.value as '' | SupportCenterAppointmentFormChannel,
                    }))
                  }
                >
                  <option value="">Selecione...</option>
                  {SUPPORT_CENTER_APPOINTMENT_FORM_CHANNEL_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </AppointmentFormField>
              {slotLocked ? (
                <>
                  <AppointmentFormField label="Data" required>
                    <input
                      type="date"
                      className={appointmentFormInputClass}
                      value={selectedDate}
                      disabled
                      readOnly
                    />
                  </AppointmentFormField>
                  <AppointmentFormField label="Horário" required>
                    <input
                      type="text"
                      className={appointmentFormInputClass}
                      value={selectedHour}
                      disabled
                      readOnly
                    />
                  </AppointmentFormField>
                </>
              ) : null}
              <AppointmentFormField
                label="Especialidade"
                required
                hint={
                  !eligibilityReady ? (
                    <Text size="xs" color="muted" className="mt-1">
                      Informe o CPF do segurado no passo 1.
                    </Text>
                  ) : !form.assistance_code.trim() ? (
                    <Text size="xs" color="muted" className="mt-1">
                      Selecione a assistência no passo 2.
                    </Text>
                  ) : catalogSpecIdsForAssistance === null ? (
                    <Text size="xs" color="muted" className="mt-1">
                      Carregando especialidades da assistência...
                    </Text>
                  ) : loadingSlotFilter ? (
                    <Text size="xs" color="muted" className="mt-1">
                      Verificando profissionais livres neste horário...
                    </Text>
                  ) : !hasEligibleSpecs ? (
                    <Text size="xs" color="muted" className="mt-1">
                      Nenhuma especialidade vinculada a esta assistência neste escopo.
                    </Text>
                  ) : slotLocked && selectableSpecializations.length === 0 ? (
                    <Text size="xs" color="muted" className="mt-1">
                      Nenhum profissional livre neste horário.
                    </Text>
                  ) : null
                }
              >
                <select
                  className={appointmentFormInputClass}
                  value={form.specialization_id}
                  disabled={
                    !form.assistance_code.trim() ||
                    catalogSpecIdsForAssistance === null ||
                    !hasEligibleSpecs ||
                    loadingSlotFilter
                  }
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      specialization_id: e.target.value,
                      professional_user_id: '',
                      scheduled_at: preserveScheduledAt(prev.scheduled_at, slotLocked),
                    }))
                  }
                >
                  <option value="">
                    {!eligibilityReady
                      ? 'Aguardando CPF do segurado...'
                      : !form.assistance_code.trim()
                        ? 'Selecione a assistência no passo 2...'
                        : catalogSpecIdsForAssistance === null
                          ? 'Carregando especialidades...'
                          : loadingSlotFilter
                            ? 'Verificando disponibilidade...'
                            : !hasEligibleSpecs
                              ? 'Sem especialidades para esta assistência'
                              : 'Selecione...'}
                  </option>
                  {selectableSpecializations.map((spec) => (
                    <option key={spec.id} value={spec.id}>
                      {spec.name}
                    </option>
                  ))}
                </select>
              </AppointmentFormField>
              <AppointmentFormField label="Profissional" required>
                <select
                  className={appointmentFormInputClass}
                  value={form.professional_user_id}
                  disabled={
                    !form.specialization_id ||
                    !eligibilityReady ||
                    loadingSlotFilter ||
                    (lockProfessionalToSelf && Boolean(form.professional_user_id))
                  }
                  onChange={(e) => {
                    const professionalId = e.target.value
                    const professional = professionals.find((p) => p.id === professionalId)
                    const nextEmail = professional?.email
                      ? String(professional.email).trim().toLowerCase()
                      : ''
                    setForm((prev) => {
                      let internal_emails = prev.internal_emails
                      if (mode === 'create' && nextEmail) {
                        // Troca de profissional: remove e-mails de outros profissionais da lista
                        // e garante o e-mail do selecionado (mantém e-mails de equipe extras).
                        const professionalEmails = new Set(
                          professionals
                            .map((p) => String(p.email ?? '').trim().toLowerCase())
                            .filter(Boolean)
                        )
                        const teamOnly = prev.internal_emails
                          .map((email) => email.trim().toLowerCase())
                          .filter((email) => email && !professionalEmails.has(email))
                        internal_emails = [nextEmail, ...teamOnly]
                      }
                      return {
                        ...prev,
                        professional_user_id: professionalId,
                        scheduled_at: preserveScheduledAt(prev.scheduled_at, slotLocked),
                        internal_emails,
                      }
                    })
                  }}
                >
                  <option value="">
                    {!form.specialization_id
                      ? 'Selecione a especialidade acima...'
                      : professionals.length === 0
                        ? slotLocked
                          ? 'Sem profissionais livres neste horário'
                          : 'Sem profissionais disponíveis'
                        : 'Selecione...'}
                  </option>
                  {professionals.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                      {p.email ? ` (${p.email})` : ''}
                    </option>
                  ))}
                </select>
              </AppointmentFormField>
              {!slotLocked ? (
                <>
                  <AppointmentFormField label="Data" required>
                    <input
                      type="date"
                      className={appointmentFormInputClass}
                      value={selectedDate}
                      min={dateBounds.minYmd}
                      max={dateBounds.maxYmd}
                      onChange={(e) => setSelectedDate(e.target.value)}
                    />
                  </AppointmentFormField>
                  <AppointmentFormField label="Horário" required>
                    <select
                      className={appointmentFormInputClass}
                      value={selectedHour}
                      disabled={!selectedDate || !form.professional_user_id || loadingSlots}
                      onChange={(e) => setSelectedHour(e.target.value)}
                    >
                      <option value="">
                        {!form.professional_user_id
                          ? 'Selecione a especialidade'
                          : !selectedDate
                            ? 'Selecione a data'
                            : loadingSlots
                              ? 'Carregando...'
                              : availableHours.length === 0
                                ? 'Sem horários livres'
                                : 'Selecione...'}
                      </option>
                      {availableHours.map((slot) => (
                        <option key={slot.horario} value={slot.horario}>
                          {slot.horario}
                        </option>
                      ))}
                    </select>
                  </AppointmentFormField>
                </>
              ) : null}
            </div>
            {(slotsError ||
              slotFilterWarning ||
              (warning && !slotsError && !slotFilterWarning)) && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
                {slotsError || slotFilterWarning || warning}
              </div>
            )}

            <div className="pt-1 border-t border-gray-100 dark:border-gray-800">
              <EmailChipsInput
                label="E-mails internos (equipe)"
                values={form.internal_emails}
                onChange={(values) => setForm((prev) => ({ ...prev, internal_emails: values }))}
                placeholder="Incluir e-mail da equipe (Enter)"
                helpText="Para convites e notificações internas. Não é e-mail do segurado."
              />
            </div>
          </AppointmentFormSection>

          {mode === 'edit' ? (
            <AppointmentFormSection
              step={5}
              title="Cancelamento do agendamento anterior"
              description="O reagendamento cancela o horário antigo. Informe o canal e o motivo que o cliente receberá."
            >
              <AppointmentFormField label="Canal do cancelamento" required>
                <select
                  className={appointmentFormInputClass}
                  value={cancellationChannel}
                  disabled={saving || loadingCancelReasons}
                  onChange={(e) =>
                    setCancellationChannel(
                      e.target.value as '' | SupportCenterAppointmentCancelChannel
                    )
                  }
                >
                  <option value="">Selecione...</option>
                  {SUPPORT_CENTER_APPOINTMENT_CANCEL_CHANNEL_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </AppointmentFormField>
              <AppointmentFormField
                label="Motivo do cancelamento"
                required
                hint={
                  <p className="mt-1 text-xs font-bold text-red-600 dark:text-red-400">
                    A descrição será a mensagem que o segurado/cliente irá receber
                  </p>
                }
              >
                {loadingCancelReasons ? (
                  <Text size="sm" className="text-gray-600 dark:text-gray-300">
                    Carregando motivos...
                  </Text>
                ) : cancelReasons.length === 0 ? (
                  <Text size="sm" className="text-gray-600 dark:text-gray-300">
                    Nenhum motivo cadastrado. Cadastre em Parâmetros &gt; Motivos de cancelamento.
                  </Text>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {cancelReasons.map((reason) => {
                      const selected = reason.id === cancelReasonId
                      return (
                        <button
                          key={reason.id}
                          type="button"
                          disabled={saving}
                          onClick={() => setCancelReasonId(reason.id)}
                          className={[
                            'w-full text-left rounded-lg border px-3 py-2 transition-colors',
                            selected
                              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30'
                              : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/60',
                          ].join(' ')}
                        >
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {reason.name}
                          </div>
                          {reason.description ? (
                            <div className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
                              {reason.description}
                            </div>
                          ) : null}
                        </button>
                      )
                    })}
                  </div>
                )}
              </AppointmentFormField>
            </AppointmentFormSection>
          ) : null}

          {error && !(eligibilityReady && !hasEligibleSpecs) && (
            <div className="rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 px-3 py-2 text-sm">
              {error}
            </div>
          )}
          </>
          ) : null}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 shrink-0">
          {createdSummary ? (
            <Button onClick={onClose}>Fechar</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={onClose} disabled={saving}>
                Cancelar
              </Button>
              <Button onClick={() => void handleSubmit()} disabled={submitDisabled}>
                {saving
                  ? 'Salvando...'
                  : mode === 'edit'
                    ? 'Reagendar'
                    : 'Criar agendamento'}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
